import { MixedOneSearchPreferenceResponse } from 'src/types/one-search/onesearch-response/OfferTeaserResponseDto';
import { intersect, pipe, rawTransform } from 'valibot';
import { MixedDlsTeaserResponse } from '../../types/dls/dls-response/ProductDto';
import {
  Badge,
  Common,
  Cta,
  Description,
  Heading,
  ImageVehicleTileData,
  Prices,
  SchemaAny,
  Tag,
} from '../VehicleTileDataSchema';
import { DlsTeaserCharacteristicsTransformer, DlsTeaserConsumptionTransformer } from '../dls/TeaserTransformer';
import {
  OneSearchPreferenceCharacteristicsTransformer,
  OneSearchPreferenceConsumptionTransformer,
} from '../onesearch/PreferenceTransformer';
import { MixedOneSearchGraphQlResponse } from '../../types/one-search/onesearch-response/OneSearchGraphQlResponse';

export type SimplePriceVehicleDataFormat = {
  common: Common;
  heading: string | undefined;
  description?: string | undefined;
  image?: {
    url?: string;
    alt?: string;
    title?: string;
  };
  tag: {
    badges: Array<{
      showIcon: boolean;
      badges?: {
        name?: string;
        description?: string;
        footnote?: string;
      };
    }>;
  };
  cta: {
    label?: string;
    srp?: string;
  };

  prices: Prices;
  mixedDlsTeaserresponse?: MixedDlsTeaserResponse;
  mixedOneSearchResponse?: MixedOneSearchGraphQlResponse;
};

export const SimplePriceCommonTransformer = pipe(
  SchemaAny,
  rawTransform<SimplePriceVehicleDataFormat, Common>(({ dataset, addIssue, NEVER }) => {
    if (dataset.value === null) {
      addIssue({
        message: 'Dataset value is null. Please provide a valid object.',
        path: [{ type: 'object', origin: 'value', input: dataset.value, key: 'value', value: dataset.value }],
      });
      return NEVER;
    }
    return dataset.value.common;
  }),
);

export const SimplePriceTagTransformer = pipe(
  SchemaAny,
  rawTransform<SimplePriceVehicleDataFormat, Tag>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for OneSearchPreferenceOfferTeaserDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    const badgesArray: Badge[] =
      dataset.value.tag.badges
        ?.filter((badge) => Boolean(badge?.badges?.name))
        .map((badge) => {
          if (!badge?.badges?.name) {
            addIssue({
              message: 'label of badge is not set',
              path: [
                {
                  type: 'object',
                  origin: 'value',
                  input: dataset.value as Record<string, unknown>,
                  key: 'value',
                  value: dataset.value,
                },
              ],
            });
            return NEVER;
          }
          return {
            name: badge?.badges.name,
            description: badge?.badges.description ?? undefined,
            footnote: badge?.badges.footnote ?? undefined,
          };
        }) ?? [];

    return {
      tag: {
        badges: [
          {
            showIcon: true,
            badges: badgesArray,
          },
        ],
      },
    };
  }),
);

export const SimplePriceImageTransformer = pipe(
  SchemaAny,
  rawTransform<SimplePriceVehicleDataFormat, ImageVehicleTileData>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for OneSearchPreferenceOfferTeaserDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const imageUrl = dataset.value.image;
    if (!imageUrl) {
      addIssue({
        message: 'Image URL is not defined',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return {
        image: {
          url: undefined,
          alt: undefined,
          title: undefined,
        },
      };
    }

    return {
      image: {
        url: imageUrl.url,
        alt: imageUrl.url,
        title: imageUrl.url,
      },
    };
  }),
);

export const SimplePriceHeadingTransformer = pipe(
  SchemaAny,
  rawTransform<SimplePriceVehicleDataFormat, Heading>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for ProductDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const headingContent = dataset.value.heading;
    if (!headingContent) {
      addIssue({
        message: 'The Heading is not defined',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
    }

    return {
      heading: {
        content: headingContent,
      },
    };
  }),
);

export const SimplePricePricesTransformer = pipe(
  SchemaAny,
  rawTransform<SimplePriceVehicleDataFormat, Prices>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OneSearchPreferencePricesTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    return dataset.value.prices;
  }),
);

export const SimplePriceCtaTransformer = pipe(
  SchemaAny,
  rawTransform<SimplePriceVehicleDataFormat, Cta>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OneSearchPreferenceCtaTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const srpLink = dataset.value.cta.srp;
    if (!srpLink) {
      addIssue({
        message: 'The SRP Link is not provided, but is important for the Teaser',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      cta: {
        label: dataset.value.cta.label,
        srp: srpLink,
      },
    };
  }),
);

export const SimplePriceVehicleDataFormatToMixedDlsTeaserTransformer = pipe(
  SchemaAny,
  rawTransform<SimplePriceVehicleDataFormat, MixedDlsTeaserResponse>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in SimplePriceVehicleDataFormatToMixedDlsTeaserTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const mixedDlsResponse = dataset.value.mixedDlsTeaserresponse;
    if (!mixedDlsResponse) {
      addIssue({
        message: 'no mixedDlsTeaserresponse available',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return mixedDlsResponse;
  }),
);

export const SimplePriceVehicleDataFormatToMixedOneSearchGraphQlResponse = pipe(
  SchemaAny,
  rawTransform<SimplePriceVehicleDataFormat, MixedOneSearchGraphQlResponse>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in SimplePriceVehicleDataFormatToMixedDlsTeaserTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const mixedPreferenceResponse = dataset.value.mixedOneSearchResponse;
    if (!mixedPreferenceResponse) {
      addIssue({
        message: 'no mixedPreferenceResponse available',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return mixedPreferenceResponse;
  }),
);

export const SimplePriceDescriptionTransformer = pipe(
  SchemaAny,
  rawTransform<SimplePriceVehicleDataFormat, Description>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OfferTeaserDataDtoDescriptionTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      description: {
        content: dataset.value.description,
      },
    };
  }),
);

export const SimplePriceOneSearchConsumptionTransformer = pipe(
  SchemaAny,
  SimplePriceVehicleDataFormatToMixedOneSearchGraphQlResponse,
  OneSearchPreferenceConsumptionTransformer,
);

export const SimplePriceOneSearchCharacteristicsTransformer = pipe(
  SchemaAny,
  SimplePriceVehicleDataFormatToMixedOneSearchGraphQlResponse,
  OneSearchPreferenceCharacteristicsTransformer,
);

export const SimplePriceDlsConsumptionTransformer = pipe(
  SchemaAny,
  SimplePriceVehicleDataFormatToMixedDlsTeaserTransformer,
  DlsTeaserConsumptionTransformer,
);

export const SimplePriceDlsCharacteristicsTransformer = pipe(
  SchemaAny,
  SimplePriceVehicleDataFormatToMixedDlsTeaserTransformer,
  DlsTeaserCharacteristicsTransformer,
);

export const ConvertSimplePriceDlsToVehicleDataTransformer = intersect([
  SimplePriceCommonTransformer,
  SimplePriceTagTransformer,
  SimplePriceImageTransformer,
  SimplePriceHeadingTransformer,
  SimplePriceDescriptionTransformer,
  SimplePricePricesTransformer,
  SimplePriceDlsCharacteristicsTransformer,
  SimplePriceDlsConsumptionTransformer,
  SimplePriceCtaTransformer,
]);

export const ConvertSimplePriceOneSearchToVehicleDataTransformer = intersect([
  SimplePriceCommonTransformer,
  SimplePriceTagTransformer,
  SimplePriceImageTransformer,
  SimplePriceHeadingTransformer,
  SimplePriceDescriptionTransformer,
  SimplePricePricesTransformer,
  SimplePriceOneSearchCharacteristicsTransformer,
  SimplePriceOneSearchConsumptionTransformer,
  SimplePriceCtaTransformer,
]);
